import React, { useState } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/uk';
import styles from "../../styles/Select.module.css";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

interface DateInputProps {
  label: string;
  value?: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  style?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  placeholder?: string;
}

export const DateInput: React.FC<DateInputProps> = ({
  label,
  value,
  onChange,
  disabled,
  style,
  labelStyle,
  placeholder = 'Оберіть дату',
}) => {
  const [focused, setFocused] = useState(false);
  const valueAsDayjs = value ? dayjs(value) : null;
  const hasValue = !!value;
  const isFloating = focused || hasValue;


  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="uk">
      <div className={styles.formControl} style={style}>
        <div className={styles.selectWrapper}>
          <DatePicker
            value={valueAsDayjs}
            disabled={disabled}
            onChange={(newValue: Dayjs | null) => {
              onChange(newValue ? newValue.format('YYYY-MM-DDTHH:mm:ssZ') : null);
            }}
            onOpen={() => setFocused(true)}
            onClose={() => setFocused(false)}
            slotProps={{
              textField: {
                placeholder: isFloating ? placeholder : '',
                variant: 'outlined',
                fullWidth: true,
                onFocus: () => setFocused(true),
                onBlur: () => setFocused(false),
                label,
                sx: {
                  '& .MuiOutlinedInput-root': {
                    height: '48px',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    backgroundColor: disabled ? 'var(--disabled-bg)' : 'var(--white)',
                    paddingRight: '8px',

                    '& fieldset': {
                      borderColor: 'var(--disabled-c)',
                      borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                      borderColor: 'var(--blue)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'var(--blue)',
                      borderWidth: '1px',
                    },
                    '&.Mui-disabled': {
                      opacity: 0.6,
                      '& fieldset': {
                        borderColor: 'var(--disabled-c)',
                      },
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '0 0 0 14px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    color: 'var(--black)',
                    '&::placeholder': {
                      color: 'var(--disabled-c)',
                      opacity: 0.7,
                      fontSize: '13px',
                    },
                  },

                  '& .MuiInputLabel-root': {
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    color: 'var(--disabled-c)',
                    fontWeight: 500,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    left: '10px',
                    transition: 'top 0.2s ease, font-size 0.2s ease, color 0.2s ease, transform 0.2s ease',
                    ...labelStyle,
                  },
                  // Label при фокусі або наявності значення — піднімається на border
                  '& .MuiInputLabel-root.Mui-focused, & .MuiInputLabel-root.MuiFormLabel-filled': {
                    top: '0',
                    fontSize: '14px',
                    transform: 'translateY(-50%)',
                    color: focused ? 'var(--blue)' : 'var(--black)',
                    backgroundColor: labelStyle?.backgroundColor ?? 'var(--white)',
                    padding: '0 4px',
                    fontWeight: labelStyle?.fontWeight ?? 700,
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'var(--blue)',
                  },

                  // Іконка календаря з відступом
                  '& .MuiInputAdornment-root': {
                    marginRight: '4px', 
                  },
                  '& .MuiInputAdornment-root .MuiIconButton-root': {
                    color: focused ? 'var(--blue)' : 'var(--disabled-c)',
                    padding: '6px',
                    marginRight: '2px', 
                    '&:hover': {
                      color: 'var(--blue)',
                      backgroundColor: 'rgba(24, 96, 217, 0.08)',
                      borderRadius: '8px',
                    },
                  },
                },
              },
              popper: {
                sx: {
                  '& .MuiPaper-root': {
                    borderRadius: '10px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    fontFamily: 'inherit',
                  },
                  '& .MuiPickersCalendarHeader-label': {
                    fontSize: '14px',
                    fontWeight: 700,
                    fontFamily: 'inherit',
                  },
                  '& .MuiDayCalendar-weekDayLabel': {
                    fontSize: '11px',
                    fontWeight: 700,
                    color: 'var(--disabled-c)',
                    fontFamily: 'inherit',
                  },
                  '& .MuiPickersDay-root': {
                    fontSize: '13px',
                    fontFamily: 'inherit',
                    borderRadius: '50%',
                    '&:hover': {
                      backgroundColor: 'rgba(24, 96, 217, 0.08)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'var(--blue)',
                      '&:hover': { backgroundColor: 'var(--blue)' },
                    },
                    '&.MuiPickersDay-today:not(.Mui-selected)': {
                      borderColor: 'var(--blue)',
                      color: 'var(--blue)',
                    },
                  },
                  '& .MuiPickersArrowSwitcher-button': {
                    color: 'var(--disabled-c)',
                    '&:hover': {
                      backgroundColor: 'rgba(24, 96, 217, 0.08)',
                      color: 'var(--blue)',
                    },
                  },
                },
              },
            }}
          />
        </div>
      </div>
    </LocalizationProvider>
  );
};